import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Seo } from "../components/Seo";
import { SlotPicker } from "../components/SlotPicker";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function RescheduleCall() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => api.get(`/acquisition/manage/${token}`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const callLabel = data?.kind === "audit" ? "Audit Review Call" : "strategy call";

  const reschedule = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/acquisition/manage/${token}/reschedule`, { slot_id: selected });
      setDone(r.data.call);
    } catch (e) { toast.error(apiError(e)); setSelected(null); load(); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0C0F] text-white">
      <Seo title="Reschedule Your Call — HughLaunch" description="Move your HughLaunch call to another open time." path="/reschedule" />
      <div className="mx-auto max-w-2xl px-5 pt-6">
        <a href="/" data-testid="resched-home-logo" className="inline-flex items-center gap-2">
          <img src="/hl-logo-v2.png" alt="HughLaunch" className="h-8 w-auto" />
          <span className="text-lg font-extrabold">Hugh<span className="text-[#C6FF00]">Launch</span></span>
        </a>
      </div>
      <div className="mx-auto max-w-2xl px-5 py-14 lg:py-20">
        {err ? (
          <div className="text-center" data-testid="resched-error">
            <h1 className="text-3xl font-extrabold">Booking not found</h1>
            <p className="mt-4 text-[#8A939B]">This reschedule link isn't valid. Email hello@hughlaunch.com and we'll sort it out.</p>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="resched-confirmed">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#C6FF00]" />
            <h1 className="mt-6 text-3xl font-extrabold sm:text-4xl">You're rescheduled.</h1>
            <p className="mx-auto mt-4 max-w-md text-[#8A939B]">Your {callLabel} is now <span className="font-bold text-white">{done.start_display}</span>. A confirmation email and updated Google Calendar invite with your Google Meet link are on their way.</p>
          </div>
        ) : !data ? (
          <p className="text-center text-sm text-[#8A939B]">Loading your booking…</p>
        ) : data.past ? (
          <div className="text-center" data-testid="resched-past">
            <h1 className="text-3xl font-extrabold">This call already happened</h1>
            <p className="mt-4 text-[#8A939B]">Want another one? Book a fresh strategy call anytime.</p>
            <a href="/book" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C6FF00] px-7 py-3.5 font-extrabold text-[#0A0C0F]">Book a New Call <ArrowRight className="h-4 w-4" /></a>
          </div>
        ) : !data.can_reschedule ? (
          <div className="text-center" data-testid="resched-limit">
            <h1 className="text-3xl font-extrabold">Reschedule already used</h1>
            <p className="mx-auto mt-4 max-w-md text-[#8A939B]">Hi {data.name.split(" ")[0]} — your {callLabel} has already been rescheduled once and is set for <span className="font-bold text-white">{data.start_display}</span>. If something urgent came up, email hello@hughlaunch.com and we'll help directly.</p>
          </div>
        ) : (
          <div data-testid="resched-form">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C6FF00]">Reschedule</p>
            <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">Move your {data.kind === "audit" ? "Audit Review Call" : "strategy call"}</h1>
            <p className="mt-4 text-[#8A939B]">Hi {data.name.split(" ")[0]} — your call is currently set for <span className="font-bold text-white">{data.start_display}</span>. Pick a new time below and we'll update everything automatically.</p>

            <p className="mt-8 mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Pick a new time</p>
            <SlotPicker slots={data.slots} selected={selected} onSelect={setSelected} testPrefix="resched-slot" />

            <button data-testid="resched-submit-btn" disabled={!selected || busy} onClick={reschedule}
              className="mt-8 w-full rounded-full bg-[#C6FF00] px-7 py-4 font-extrabold text-[#0A0C0F] disabled:opacity-30">
              {busy ? "Moving your call…" : <>Confirm New Time <ArrowRight className="ml-1 inline h-4 w-4" /></>}
            </button>
            <p className="mt-4 text-center text-xs text-[#5b636b]">All times Pacific. You can reschedule once — your old time is released and both calendars update automatically.</p>
          </div>
        )}
      </div>
    </div>
  );
}
